import { useAutoAnimate } from '@formkit/auto-animate/react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/20/solid';
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import AccessoryDrawer from 'components/accessories-page/accessory-drawer';
import AccessoryTypePanel from 'components/accessories-page/accessory-type-panel';
import AdditionalAccessoriesDrawer from 'components/accessories-page/additional-accessories-drawer';
import DuctedAccessoriesPage from 'components/accessories-page/ducted/ducted-accessories-page';
import DuctedIncludeAllRecommendedToggle from 'components/accessories-page/ducted/ducted-include-all-recommended-toggle';
import IncludeAllRecommendedToggle from 'components/accessories-page/include-all-recommended-toggle';
import LineSetOption from 'components/accessories-page/line-set-option';
import SelectedSystemComponent from 'components/common/selected-system';
import DuctedSystemOptionComponent from 'components/equipment-page/ducted-system-option';
import InternalNote from 'components/layout/internal-note';
import Tooltip from 'components/layout/tooltip';
import DuctRunSuppliesGroup from 'components/systems/duct-run-supplies-group';
import { useFooterConfig } from 'contexts/footer-context';
import { accessoryCategories } from 'data/constants';
import { AccessoryCategoryType, AccessoryProductType } from 'data/types';
import {
  useGetQuotableSystemsAccessoriesRecommendationsQuery,
  useGetQuotableSystemsQuery,
  useGetSystemAccessoryTypesQuery,
  useUpdateQuotableSystemAccessoriesMutation,
  useUpdateQuotableSystemAccessoriesReviewedMutation
} from 'features/api/project-api';
import {
  setActiveSelectedSystem,
  setSelectedDuctedSystems,
  setSelectedSystems,
  setSystemAccessoriesReviewed,
  updateSelectedDuctedSystem,
  updateSelectedSystem
} from 'features/systems';
import {
  setActiveAccessoryDrawer,
  setActiveTab,
  setCategoriesWithUnselectedRecommendations,
  setZoneDuctMode
} from 'features/ui';
import { useManualAccessories } from 'hooks/use-manual-accessories';
import { useIsSystemQuoted } from 'hooks/use-quoted-unique-ids';
import { useStepTipSequence } from 'hooks/use-step-tip-sequence';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import { useUnquoteOnAccessoryChange } from 'hooks/use-unquote-on-accessory-change';
import { produce } from 'immer';
import {
  ReactNode,
  createElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { getQuotableAccessories } from 'utils/accessories';
import { collectSingleChoiceAlternativesToClear } from 'utils/accessory-option-groups';
import type { AccessoryQuantityChangeType } from 'utils/accessory-option-groups-types';
import { getAccessoriesTipSections } from 'utils/accessories-tips';
import { applyAccessoryQuantityToCondenser } from 'utils/condenser-accessory-selection';
import {
  areAllRecommendedAccessoriesSelected,
  buildZoneSelectedAccessories,
  collectCartableRecommendedAccessories,
  isZoneEquipmentId
} from 'utils/include-recommended-accessories';
import axios from 'utils/api';
import { usesDuctedAccessoryRules } from 'utils/ducted-accessory-rules';
import { distributeQuantityAcrossRecommendations } from 'utils/distribute-quantity';
import { DUCT_SOURCING_NOTICE } from 'utils/duct-component-fallback';
import {
  buildDuctingComponentRecommendations,
  buildZoneDuctModeOptions
} from 'utils/ducting-accessory-recommendations';
import { resolveDuctingComponentZoneQuantities } from 'utils/ducting-component-save-attribution';
import {
  DUCT_MODE_KEYS,
  RadialDuctModeKeyType,
  isConcealedDuctProduct
} from 'utils/ducting-products';
import { condenserAccessoryEntries } from 'utils/manual-accessories';
import { getEmptyComparisonBackTarget } from 'utils/project';
import { isSystemDesignLocked } from 'utils/system-design-bypass';
import { conflictModal, errorModal } from 'utils/ui';
import { ZONE_DUCT_MODE_LABELS } from 'utils/zone-duct-modes';

export default function AccessoriesPage() {
  const dispatch = useAppDispatch();
  const {
    selectedSystems,
    selectedDuctedSystems,
    activeSelectedSystem,
    activeDuctedSystem,
    activeSystemType,
    quotableSystems,
    systemsToCompare
  } = useAppSelector((state) => state.systems);
  const isSystemQuoted = useIsSystemQuoted();
  const { unquoteDuctless, unquoteDucted } = useUnquoteOnAccessoryChange();
  const { projectSettings } = useAppSelector((state) => state.project);
  const [animate] = useAutoAnimate({ duration: 100 });
  const [updateQuotableSystemAccessories] =
    useUpdateQuotableSystemAccessoriesMutation();
  const [updateAccessoriesReviewed] =
    useUpdateQuotableSystemAccessoriesReviewedMutation();

  const systemUniqueIds = selectedSystems.map((s) => s.uniqueId);
  const {
    data: apiAccessoryRecommendations,
    isLoading: isLoadingRecommendations,
    isFetching: isFetchingRecommendations
  } = useGetQuotableSystemsAccessoriesRecommendationsQuery(
    { uniqueIds: systemUniqueIds },
    { skip: systemUniqueIds.length === 0 }
  );
  const { configurations } = useAppSelector((state) => state.configurations);
  const { ductingKitProducts, airHandlerProducts, ductingComponents } =
    useAppSelector((state) => state.data);
  const zoneDuctModes = useAppSelector((state) => state.ui.zoneDuctModes);

  const accessoryRecommendations = useMemo(() => {
    if (!apiAccessoryRecommendations) return apiAccessoryRecommendations;
    const { recommendations: componentRecommendations, demotedKits } =
      buildDuctingComponentRecommendations({
        systems: selectedSystems,
        configurations,
        ductingKitProducts,
        airHandlerProducts,
        ductingComponents,
        zoneDuctModes
      });
    const withDemotions = apiAccessoryRecommendations.map((rec) =>
      rec.recommendedPriority === 1 &&
      demotedKits.some(
        (demoted) =>
          demoted.zoneUniqueId === rec.uniqueId &&
          demoted.kitProductId === rec.accessoryProductId
      )
        ? { ...rec, recommendedPriority: 2 }
        : rec
    );
    return [...withDemotions, ...componentRecommendations];
  }, [
    apiAccessoryRecommendations,
    selectedSystems,
    configurations,
    ductingKitProducts,
    airHandlerProducts,
    ductingComponents,
    zoneDuctModes
  ]);
  const { data: systemAccessoryTypes, isLoading: isLoadingAccessoryTypes } =
    useGetSystemAccessoryTypesQuery();
  const { data: quotableSystemsData } = useGetQuotableSystemsQuery(
    systemUniqueIds,
    { skip: systemUniqueIds.length === 0 }
  );

  const isLoadingAccessories =
    isLoadingRecommendations ||
    isLoadingAccessoryTypes ||
    isFetchingRecommendations;

  const accessoryTypesCompleted = useMemo(() => {
    if (!systemAccessoryTypes || !accessoryRecommendations) return [];

    const condensers =
      activeSystemType === 'ductless'
        ? selectedSystems[activeSelectedSystem]?.configuration.condensers ?? []
        : selectedDuctedSystems[activeDuctedSystem]?.config.condensers ?? [];

    return systemAccessoryTypes
      .filter((type) => {
        const typeAccessoryIds = new Set(
          accessoryRecommendations
            .filter((r) => r.accessoryType === type.code)
            .map((r) => r.accessoryProductId)
        );
        return condensers.some((condenser) =>
          condenser.selectedAccessories.some(
            (acc) => typeAccessoryIds.has(acc.productId) && acc.quantity > 0
          )
        );
      })
      .map((type) => type.code);
  }, [
    systemAccessoryTypes,
    accessoryRecommendations,
    activeSystemType,
    selectedSystems,
    selectedDuctedSystems,
    activeSelectedSystem,
    activeDuctedSystem
  ]);

  const [airHandlerAccessories, setAirHandlerAccessories] = useState<
    Record<string, Record<number, number>>
  >({});
  const eqInteractedRef = useRef(false);

  useEffect(() => {
    setAirHandlerAccessories({});
    eqInteractedRef.current = false;
  }, [activeSelectedSystem, activeDuctedSystem]);

  useEffect(() => {
    if (
      activeSystemType === 'ductless' &&
      selectedSystems.length > 0 &&
      activeSelectedSystem >= selectedSystems.length
    ) {
      dispatch(
        setActiveSelectedSystem(Math.max(0, selectedSystems.length - 1))
      );
    }
  }, [
    selectedSystems.length,
    activeSelectedSystem,
    activeSystemType,
    dispatch
  ]);

  const accessoryTypePanelData = useMemo(() => {
    if (!systemAccessoryTypes || !accessoryRecommendations) return [];

    const condensers =
      activeSystemType === 'ductless'
        ? selectedSystems[activeSelectedSystem]?.configuration.condensers ?? []
        : selectedDuctedSystems[activeDuctedSystem]?.config.condensers ?? [];

    const activeSystemUniqueId =
      activeSystemType === 'ductless'
        ? selectedSystems[activeSelectedSystem]?.uniqueId
        : undefined;

    // Get first condenser for system-level accessories
    const firstCondenser = condensers[0];

    const equipmentItems: Array<{
      productType: 'condenser' | 'air_handler';
      uniqueId: string;
      name: string;
      condenserUniqueId: string;
      selectedAccessories: (typeof condensers)[0]['selectedAccessories'];
    }> = [];

    let airHandlerCount = 0;

    const accessoryFallbackByProductId = new Map<
      number,
      (typeof condensers)[0]['selectedAccessories'][number]
    >();
    accessoryRecommendations.forEach((rec) => {
      if (!accessoryFallbackByProductId.has(rec.accessoryProductId)) {
        accessoryFallbackByProductId.set(rec.accessoryProductId, {
          productId: rec.accessoryProductId,
          name: rec.accessoryName,
          description: rec.accessoryDescription,
          price: rec.accessoryPrice,
          type: rec.accessoryOriginalType as AccessoryProductType['type'],
          productUrl: rec.accessoryProductUrl,
          photo: rec.accessoryPhoto,
          availableStock: rec.accessoryAvailableStock,
          quantity: 0
        });
      }
    });

    condensers.forEach((condenser) => {
      equipmentItems.push({
        productType: 'condenser',
        uniqueId: condenser.uniqueId,
        name: condenser.name,
        condenserUniqueId: condenser.uniqueId,
        selectedAccessories: condenser.selectedAccessories
      });

      condenser.zones.forEach((zone) => {
        airHandlerCount += 1;
        const zoneAccMap = airHandlerAccessories[zone.uniqueId] ?? {};
        equipmentItems.push({
          productType: 'air_handler',
          uniqueId: zone.uniqueId,
          name: `Air Handler ${airHandlerCount}`,
          condenserUniqueId: condenser.uniqueId,
          selectedAccessories: buildZoneSelectedAccessories({
            zoneAccMap,
            condenserAccessories: condenser.selectedAccessories,
            fallbackByProductId: accessoryFallbackByProductId
          })
        });
      });
    });

    return systemAccessoryTypes
      .filter((type) => {
        return accessoryRecommendations.some(
          (rec) => rec.accessoryType === type.code
        );
      })
      .map((type) => {
        if (type.limitOnePerSystem) {
          const systemRecs = accessoryRecommendations.filter(
            (rec) =>
              (!activeSystemUniqueId ||
                rec.systemUniqueId === activeSystemUniqueId) &&
              rec.productType === 'system' &&
              rec.accessoryType === type.code
          );

          if (systemRecs.length === 0) {
            return {
              accessoryType: type,
              equipment: [],
              isCompleted: accessoryTypesCompleted.includes(type.code)
            };
          }

          const accessoryProducts = new Map<
            number,
            AccessoryProductType & { shortName?: string }
          >();
          systemRecs.forEach((rec) => {
            accessoryProducts.set(rec.accessoryProductId, {
              productId: rec.accessoryProductId,
              name: rec.accessoryName,
              shortName: rec.accessoryShortName,
              description: rec.accessoryDescription,
              price: rec.accessoryPrice,
              type: rec.accessoryOriginalType as AccessoryProductType['type'],
              productUrl: rec.accessoryProductUrl,
              photo: rec.accessoryPhoto,
              availableStock: rec.accessoryAvailableStock
            });
          });

          return {
            accessoryType: type,
            equipment: [
              {
                equipmentUniqueId: firstCondenser?.uniqueId ?? '',
                equipmentName: 'System',
                condenserUniqueId: firstCondenser?.uniqueId ?? '',
                recommendations: systemRecs,
                selectedAccessories: firstCondenser?.selectedAccessories ?? [],
                accessoryProducts
              }
            ],
            isCompleted: accessoryTypesCompleted.includes(type.code)
          };
        }

        const systemScopedRecs = accessoryRecommendations.filter(
          (rec) =>
            (!activeSystemUniqueId ||
              rec.systemUniqueId === activeSystemUniqueId) &&
            rec.productType === 'system' &&
            rec.accessoryType === type.code
        );

        const equipmentWithAccessories = equipmentItems
          .map((item) => {
            const itemRecs = accessoryRecommendations.filter(
              (rec) =>
                (!activeSystemUniqueId ||
                  rec.systemUniqueId === activeSystemUniqueId) &&
                rec.uniqueId === item.uniqueId &&
                rec.productType === item.productType &&
                rec.accessoryType === type.code
            );

            if (itemRecs.length === 0) return null;

            const accessoryProducts = new Map<
              number,
              AccessoryProductType & { shortName?: string }
            >();
            itemRecs.forEach((rec) => {
              accessoryProducts.set(rec.accessoryProductId, {
                productId: rec.accessoryProductId,
                name: rec.accessoryName,
                shortName: rec.accessoryShortName,
                description: rec.accessoryDescription,
                price: rec.accessoryPrice,
                type: rec.accessoryOriginalType as AccessoryProductType['type'],
                productUrl: rec.accessoryProductUrl,
                photo: rec.accessoryPhoto,
                availableStock: rec.accessoryAvailableStock
              });
            });

            return {
              equipmentUniqueId: item.uniqueId,
              equipmentName: item.name,
              condenserUniqueId: item.condenserUniqueId,
              recommendations: itemRecs,
              selectedAccessories: item.selectedAccessories,
              accessoryProducts
            };
          })
          .filter(
            (
              e
            ): e is {
              equipmentUniqueId: string;
              equipmentName: string;
              condenserUniqueId: string;
              recommendations: typeof accessoryRecommendations;
              selectedAccessories: (typeof condensers)[0]['selectedAccessories'];
              accessoryProducts: Map<number, AccessoryProductType>;
            } => e !== null
          );

        if (systemScopedRecs.length > 0 && firstCondenser) {
          const systemAccessoryProducts = new Map<
            number,
            AccessoryProductType & { shortName?: string }
          >();
          systemScopedRecs.forEach((rec) => {
            systemAccessoryProducts.set(rec.accessoryProductId, {
              productId: rec.accessoryProductId,
              name: rec.accessoryName,
              shortName: rec.accessoryShortName,
              description: rec.accessoryDescription,
              price: rec.accessoryPrice,
              type: rec.accessoryOriginalType as AccessoryProductType['type'],
              productUrl: rec.accessoryProductUrl,
              photo: rec.accessoryPhoto,
              availableStock: rec.accessoryAvailableStock
            });
          });
          equipmentWithAccessories.push({
            equipmentUniqueId: firstCondenser.uniqueId,
            equipmentName: 'System',
            condenserUniqueId: firstCondenser.uniqueId,
            recommendations: systemScopedRecs,
            selectedAccessories: firstCondenser.selectedAccessories,
            accessoryProducts: systemAccessoryProducts
          });
        }

        return {
          accessoryType: type,
          equipment: equipmentWithAccessories,
          isCompleted: accessoryTypesCompleted.includes(type.code)
        };
      })
      .filter((data) => data.equipment.length > 0);
  }, [
    systemAccessoryTypes,
    accessoryRecommendations,
    activeSystemType,
    selectedSystems,
    selectedDuctedSystems,
    activeSelectedSystem,
    activeDuctedSystem,
    accessoryTypesCompleted,
    airHandlerAccessories
  ]);

  useEffect(() => {
    if (eqInteractedRef.current) return;
    if (!accessoryRecommendations) return;

    const condensers =
      activeSystemType === 'ductless'
        ? selectedSystems[activeSelectedSystem]?.configuration.condensers ?? []
        : selectedDuctedSystems[activeDuctedSystem]?.config.condensers ?? [];

    const newMap: Record<string, Record<number, number>> = {};

    const activeSystemUniqueId =
      activeSystemType === 'ductless'
        ? selectedSystems[activeSelectedSystem]?.uniqueId
        : selectedDuctedSystems[activeDuctedSystem]?.id?.toString();

    const activeQs = quotableSystemsData?.find(
      (qs) => qs.uniqueId === activeSystemUniqueId
    );

    const dbMappedProducts = new Set<number>();

    if (activeQs) {
      activeQs.accessories.forEach((acc) => {
        if (acc.zoneUniqueId && acc.quantity > 0) {
          newMap[acc.zoneUniqueId] = newMap[acc.zoneUniqueId] ?? {};
          newMap[acc.zoneUniqueId][acc.productId] =
            (newMap[acc.zoneUniqueId][acc.productId] ?? 0) + acc.quantity;
          dbMappedProducts.add(acc.productId);
        }
      });
    }

    condensers.forEach((cond) => {
      const zoneIds = cond.zones.map((z) => z.uniqueId);

      const productQtyMap = new Map<number, number>();
      cond.selectedAccessories.forEach((acc) => {
        if (acc.quantity <= 0) return;
        if (dbMappedProducts.has(acc.productId)) return;
        productQtyMap.set(
          acc.productId,
          (productQtyMap.get(acc.productId) ?? 0) + acc.quantity
        );
      });

      productQtyMap.forEach((totalQty, productId) => {
        const airHandlerRecs = accessoryRecommendations.filter(
          (r) =>
            r.accessoryProductId === productId &&
            r.productType === 'air_handler' &&
            zoneIds.includes(r.uniqueId)
        );

        if (airHandlerRecs.length > 0) {
          distributeQuantityAcrossRecommendations(
            totalQty,
            airHandlerRecs
          ).forEach(({ uniqueId, quantity }) => {
            newMap[uniqueId] = newMap[uniqueId] ?? {};
            newMap[uniqueId][productId] =
              (newMap[uniqueId][productId] ?? 0) + quantity;
          });
        }
      });
    });

    setAirHandlerAccessories(newMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accessoryRecommendations,
    selectedSystems,
    selectedDuctedSystems,
    activeSelectedSystem,
    activeDuctedSystem,
    activeSystemType,
    quotableSystemsData
  ]);

  useEffect(() => {
    dispatch(setActiveAccessoryDrawer(null));
  }, [activeSystemType]);

  useEffect(() => {
    if (
      selectedDuctedSystems[activeDuctedSystem]?.config.condensers.some(
        (condenser) =>
          condenser.type === 'ducted' &&
          condenser.ductedAccessories.length === 0
      )
    ) {
      axios
        .post('ducted-system-accessories', {
          sqft:
            projectSettings.find((ps) => ps.code === 'square_footage')?.value ??
            0,
          products: selectedDuctedSystems[activeDuctedSystem].products
        })
        .then((res) => res.data)
        .then((data: AccessoryProductType[]) => {
          dispatch(
            setSelectedDuctedSystems(
              produce(selectedDuctedSystems, (draft) => {
                draft[activeDuctedSystem].config.condensers
                  .filter((cond) => cond.type === 'ducted')
                  .forEach((condenser) => {
                    // eslint-disable-next-line no-param-reassign
                    condenser.ductedAccessories = data;
                  });
              })
            )
          );
        })
        .catch(() => {
          errorModal('Failed to fetch ducted accessories.');
        });
    }
  }, [selectedDuctedSystems, activeDuctedSystem]);

  const lineSetPanelData = useMemo(() => {
    return accessoryTypePanelData.find(
      (d) => d.accessoryType.code === 'line_set'
    );
  }, [accessoryTypePanelData]);

  const nonLineSetPanelData = useMemo(() => {
    return accessoryTypePanelData.filter(
      (d) => d.accessoryType.code !== 'line_set'
    );
  }, [accessoryTypePanelData]);

  // Build a map of equipmentUniqueId -> Set<accessoryTypeCode> for accessories
  // that are included with the selected (or recommended) line set
  const includedAccessoryTypesMap = useMemo(() => {
    const map = new Map<string, Set<string>>();
    if (!lineSetPanelData) return map;

    lineSetPanelData.equipment.forEach((item) => {
      // Find the currently selected line set for this equipment
      const selectedLineSetRec = item.recommendations.find((r) => {
        const product = item.accessoryProducts.get(r.accessoryProductId);
        if (product?.type !== 'line_set') return false;
        return item.selectedAccessories.some(
          (acc) => acc.productId === r.accessoryProductId && acc.quantity >= 1
        );
      });

      // Fall back to the recommended line set if none is selected
      const lineSetRec =
        selectedLineSetRec ??
        item.recommendations.find((r) => {
          if (r.recommendedPriority !== 1) return false;
          const product = item.accessoryProducts.get(r.accessoryProductId);
          return product?.type === 'line_set';
        });

      const settings = lineSetRec?.lineSetSettings;
      if (!settings) return;

      const included = new Set<string>();
      if (settings.drainTubingIncluded) included.add('drain_hose');
      if (settings.msWireIncluded) included.add('wire');

      if (included.size > 0) {
        map.set(item.equipmentUniqueId, included);
      }
    });

    return map;
  }, [lineSetPanelData]);

  const hasSelectableLineSets = useMemo(() => {
    if (!lineSetPanelData) return false;
    return lineSetPanelData.equipment.some((item) =>
      Array.from(item.accessoryProducts.values()).some(
        (p) => p.type === 'line_set' || p.type === 'wire'
      )
    );
  }, [lineSetPanelData]);

  const activeCondensers = useMemo(
    () =>
      activeSystemType === 'ductless'
        ? selectedSystems[activeSelectedSystem]?.configuration?.condensers ?? []
        : selectedDuctedSystems[activeDuctedSystem]?.config.condensers ?? [],
    [
      activeSystemType,
      selectedSystems,
      activeSelectedSystem,
      selectedDuctedSystems,
      activeDuctedSystem
    ]
  );

  const hasCondensers = activeCondensers.length > 0;

  const lineSetRecommendedAccessories = useMemo(() => {
    if (!lineSetPanelData) return [];
    return lineSetPanelData.equipment.flatMap((item) =>
      item.recommendations
        .filter((r) => r.recommendedPriority === 1)
        .map((r) => ({
          condenserUniqueId: item.condenserUniqueId,
          equipmentUniqueId: item.equipmentUniqueId,
          accessory: item.accessoryProducts.get(r.accessoryProductId)
        }))
        .filter(
          (
            x
          ): x is {
            condenserUniqueId: string;
            equipmentUniqueId: string;
            accessory: AccessoryProductType;
          } => x.accessory !== undefined
        )
    );
  }, [lineSetPanelData]);

  // Filter out accessories that are included with their line set
  // (these should not be added to the cart when "Include Recommended" is toggled)
  const lineSetCartableRecommendedAccessories = useMemo(() => {
    if (!lineSetPanelData) return [];
    return lineSetRecommendedAccessories.filter(
      ({ equipmentUniqueId, accessory }) => {
        const equipment = lineSetPanelData.equipment.find(
          (e) => e.equipmentUniqueId === equipmentUniqueId
        );
        if (!equipment) return true;

        // Find the recommended line set's lineSetSettings for this zone
        const recommendedLineSetRec = equipment.recommendations.find((r) => {
          if (r.recommendedPriority !== 1) return false;
          const product = equipment.accessoryProducts.get(r.accessoryProductId);
          return product?.type === 'line_set';
        });

        const settings = recommendedLineSetRec?.lineSetSettings;
        if (!settings) return true;

        // If this accessory is wire and wire is included, exclude from cart
        if (accessory.type === 'wire' && settings.msWireIncluded) return false;
        // If this accessory is drain hose and drain tubing is included, exclude from cart
        if (accessory.type === 'drain_hose' && settings.drainTubingIncluded)
          return false;

        return true;
      }
    );
  }, [lineSetPanelData, lineSetRecommendedAccessories]);

  const lineSetAllRecommendedSelected = useMemo(() => {
    if (!lineSetPanelData || lineSetRecommendedAccessories.length === 0) {
      return false;
    }
    return areAllRecommendedAccessoriesSelected({
      panels: [lineSetPanelData],
      hasSelectableLineSets: true,
      includedAccessoryTypes: includedAccessoryTypesMap,
      airHandlerAccessories
    });
  }, [
    lineSetPanelData,
    lineSetRecommendedAccessories,
    includedAccessoryTypesMap,
    airHandlerAccessories
  ]);

  const lineSetSwitchDisabled = lineSetRecommendedAccessories.length === 0;

  const [lineSetIncludeRecommended, setLineSetIncludeRecommended] =
    useState(false);
  const lineSetIncludeInitRef = useRef(false);

  useEffect(() => {
    lineSetIncludeInitRef.current = false;
    setLineSetIncludeRecommended(false);
  }, [activeSelectedSystem, activeDuctedSystem]);

  useEffect(() => {
    if (
      !lineSetIncludeInitRef.current &&
      lineSetRecommendedAccessories.length > 0
    ) {
      lineSetIncludeInitRef.current = true;
      setLineSetIncludeRecommended(lineSetAllRecommendedSelected);
    }
  }, [lineSetRecommendedAccessories, lineSetAllRecommendedSelected]);

  const allRecommendedIncluded = useMemo(
    () =>
      areAllRecommendedAccessoriesSelected({
        panels: accessoryTypePanelData,
        hasSelectableLineSets,
        includedAccessoryTypes: includedAccessoryTypesMap,
        airHandlerAccessories
      }),
    [
      accessoryTypePanelData,
      hasSelectableLineSets,
      includedAccessoryTypesMap,
      airHandlerAccessories
    ]
  );

  const categoriesWithUnselectedRecommendations = useMemo(() => {
    return accessoryTypePanelData
      .filter(
        (panelData) =>
          (panelData.accessoryType.code !== 'line_set' ||
            hasSelectableLineSets) &&
          panelData.equipment.length > 0 &&
          !panelData.isCompleted &&
          panelData.equipment.some((item) =>
            item.recommendations.some((r) => r.recommendedPriority === 1)
          )
      )
      .map((panelData) => panelData.accessoryType.label);
  }, [accessoryTypePanelData, hasSelectableLineSets]);

  useEffect(() => {
    dispatch(
      setCategoriesWithUnselectedRecommendations(
        categoriesWithUnselectedRecommendations
      )
    );
  }, [categoriesWithUnselectedRecommendations, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(setCategoriesWithUnselectedRecommendations([]));
    };
  }, [dispatch]);

  useEffect(() => {
    if (
      activeSystemType !== 'ductless' ||
      isLoadingAccessories ||
      !systemAccessoryTypes ||
      !accessoryRecommendations ||
      categoriesWithUnselectedRecommendations.length > 0
    ) {
      return;
    }
    const currentSystem = selectedSystems[activeSelectedSystem];
    if (!currentSystem || isSystemQuoted(currentSystem)) return;
    if (quotableSystems[currentSystem.uniqueId]?.accessoriesReviewed ?? false) {
      return;
    }
    dispatch(
      setSystemAccessoriesReviewed({
        uniqueId: currentSystem.uniqueId,
        accessoriesReviewed: true
      })
    );
    updateAccessoriesReviewed({
      uniqueId: currentSystem.uniqueId,
      accessoriesReviewed: true
    }).catch((error) => {
      console.error('Failed to update accessories reviewed:', error);
    });
  }, [
    activeSystemType,
    isLoadingAccessories,
    systemAccessoryTypes,
    accessoryRecommendations,
    categoriesWithUnselectedRecommendations,
    selectedSystems,
    activeSelectedSystem,
    isSystemQuoted,
    quotableSystems,
    dispatch,
    updateAccessoriesReviewed
  ]);

  const buildAccessorySavePayload = (
    system: (typeof selectedSystems)[number],
    eqState?: Record<string, Record<number, number>>
  ) => {
    const effectiveEqState = eqState ?? airHandlerAccessories;
    const ductingComponentIds = new Set(
      ductingComponents.map((component) => component.productId)
    );
    const savedSystemAccessoryRows =
      quotableSystemsData?.find((qs) => qs.uniqueId === system.uniqueId)
        ?.accessories ?? [];
    const result: Array<{
      productId: number;
      quantity: number;
      configurationItemUniqueId?: string;
      productTypeCode:
        | 'condenser_accessory'
        | 'air_handler_accessory'
        | 'accessory';
    }> = [];

    system.configuration.condensers.forEach((cond) => {
      const zoneUniqueIds = cond.zones.map((z) => z.uniqueId);

      cond.selectedAccessories.forEach((acc) => {
        const isTrackedPerZone = cond.zones.some(
          (z) => effectiveEqState[z.uniqueId]?.[acc.productId] !== undefined
        );

        if (isTrackedPerZone) {
          cond.zones.forEach((zone) => {
            const qty = effectiveEqState[zone.uniqueId]?.[acc.productId] ?? 0;
            if (qty > 0) {
              result.push({
                productId: acc.productId,
                quantity: qty,
                configurationItemUniqueId: zone.uniqueId,
                productTypeCode: 'air_handler_accessory'
              });
            }
          });
          return;
        }

        const condenserRec = accessoryRecommendations?.find(
          (r) =>
            r.accessoryProductId === acc.productId &&
            r.productType === 'condenser' &&
            r.uniqueId === cond.uniqueId
        );

        if (condenserRec) {
          result.push({
            productId: acc.productId,
            quantity: acc.quantity,
            configurationItemUniqueId: cond.uniqueId,
            productTypeCode: 'condenser_accessory'
          });
          return;
        }

        const systemRec = accessoryRecommendations?.find(
          (r) =>
            r.accessoryProductId === acc.productId && r.productType === 'system'
        );

        if (systemRec) {
          result.push({
            productId: acc.productId,
            quantity: acc.quantity,
            productTypeCode: 'accessory'
          });
          return;
        }

        const airHandlerRecs = (accessoryRecommendations ?? []).filter(
          (r) =>
            r.accessoryProductId === acc.productId &&
            r.productType === 'air_handler' &&
            zoneUniqueIds.includes(r.uniqueId)
        );

        if (airHandlerRecs.length > 0) {
          distributeQuantityAcrossRecommendations(
            acc.quantity,
            airHandlerRecs
          ).forEach(({ uniqueId, quantity }) => {
            result.push({
              productId: acc.productId,
              quantity,
              configurationItemUniqueId: uniqueId,
              productTypeCode: 'air_handler_accessory'
            });
          });
          return;
        }

        if (ductingComponentIds.has(acc.productId)) {
          const savedZoneQuantities = resolveDuctingComponentZoneQuantities(
            acc.quantity,
            savedSystemAccessoryRows,
            acc.productId,
            zoneUniqueIds
          );
          if (savedZoneQuantities.length > 0) {
            savedZoneQuantities.forEach(({ zoneUniqueId, quantity }) => {
              result.push({
                productId: acc.productId,
                quantity,
                configurationItemUniqueId: zoneUniqueId,
                productTypeCode: 'air_handler_accessory'
              });
            });
            return;
          }
          const concealedZone = cond.zones.find((zone) =>
            isConcealedDuctProduct(zone.selectedProduct)
          );
          if (concealedZone) {
            result.push({
              productId: acc.productId,
              quantity: acc.quantity,
              configurationItemUniqueId: concealedZone.uniqueId,
              productTypeCode: 'air_handler_accessory'
            });
            return;
          }
        }

        result.push({
          productId: acc.productId,
          quantity: acc.quantity,
          configurationItemUniqueId: cond.uniqueId,
          productTypeCode: 'condenser_accessory'
        });
      });
    });

    return result;
  };

  const handleIncludeAllRecommended = (include: boolean) => {
    setLineSetIncludeRecommended(include);
    if (activeSystemType !== 'ductless') {
      return;
    }

    const recommendedChanges: AccessoryQuantityChangeType[] =
      collectCartableRecommendedAccessories({
        panels: accessoryTypePanelData,
        includedAccessoryTypes: includedAccessoryTypesMap
      }).map((entry) => ({
        condenserUniqueId: entry.condenserUniqueId,
        equipmentUniqueId: entry.equipmentUniqueId,
        accessory: entry.accessory,
        quantity: include ? entry.recommendedQuantity : 0
      }));
    const allRecommendedAccessories = include
      ? [
          ...recommendedChanges,
          ...collectSingleChoiceAlternativesToClear(accessoryTypePanelData)
        ]
      : recommendedChanges;
    const condensers =
      selectedSystems[activeSelectedSystem]?.configuration.condensers ?? [];

    const newAirHandlerAcc: Record<string, Record<number, number>> = {
      ...airHandlerAccessories
    };
    allRecommendedAccessories.forEach(
      ({ equipmentUniqueId, accessory, quantity }) => {
        if (isZoneEquipmentId(condensers, equipmentUniqueId)) {
          newAirHandlerAcc[equipmentUniqueId] = {
            ...(newAirHandlerAcc[equipmentUniqueId] ?? {}),
            [accessory.productId]: quantity
          };
        }
      }
    );
    setAirHandlerAccessories(newAirHandlerAcc);
    eqInteractedRef.current = true;

    const condenserTotals = new Map<string, Map<number, number>>();
    allRecommendedAccessories.forEach(
      ({ condenserUniqueId, equipmentUniqueId, accessory }) => {
        if (isZoneEquipmentId(condensers, equipmentUniqueId)) {
          const condenser = condensers.find(
            (c) => c.uniqueId === condenserUniqueId
          );
          if (condenser) {
            const total = condenser.zones.reduce((sum, z) => {
              return (
                sum + (newAirHandlerAcc[z.uniqueId]?.[accessory.productId] ?? 0)
              );
            }, 0);
            if (!condenserTotals.has(condenserUniqueId)) {
              condenserTotals.set(condenserUniqueId, new Map());
            }
            condenserTotals
              .get(condenserUniqueId)!
              .set(accessory.productId, total);
          }
        }
      }
    );

    const updatedSystems = produce(selectedSystems, (draft) => {
      allRecommendedAccessories.forEach(
        ({
          condenserUniqueId,
          equipmentUniqueId,
          accessory,
          quantity: entryQuantity
        }) => {
          const condenser = draft[
            activeSelectedSystem
          ]?.configuration.condensers.find(
            (c) => c.uniqueId === condenserUniqueId
          );
          if (condenser) {
            const quantity = isZoneEquipmentId(condensers, equipmentUniqueId)
              ? condenserTotals
                  .get(condenserUniqueId)
                  ?.get(accessory.productId) ?? entryQuantity
              : entryQuantity;
            const accessoryIndex = condenser.selectedAccessories.findIndex(
              (acc) => acc.productId === accessory.productId
            );
            if (accessoryIndex !== -1) {
              if (quantity > 0) {
                // eslint-disable-next-line no-param-reassign
                condenser.selectedAccessories[accessoryIndex].quantity =
                  quantity;
              } else {
                condenser.selectedAccessories.splice(accessoryIndex, 1);
              }
            } else if (quantity > 0) {
              condenser.selectedAccessories.push({ ...accessory, quantity });
            }
          }
        }
      );
    });
    dispatch(setSelectedSystems(updatedSystems));

    const updatedActiveSystem = updatedSystems[activeSelectedSystem];
    unquoteDuctless(updatedActiveSystem);
    updateQuotableSystemAccessories({
      uniqueId: updatedActiveSystem.uniqueId,
      accessories: buildAccessorySavePayload(
        updatedActiveSystem,
        newAirHandlerAcc
      )
    }).catch((error) => {
      console.error('Failed to update quotable system accessories:', error);
    });

    dispatch(
      updateSelectedSystem({
        uniqueId: updatedActiveSystem.uniqueId,
        quotableAccessories: getQuotableAccessories(updatedActiveSystem)
      })
    );
  };

  const handleIncludeRecommendedToggle = (
    _accessoryTypeCode: string,
    _includeRecommended: boolean,
    recommendedAccessories: AccessoryQuantityChangeType[]
  ) => {
    if (activeSystemType === 'ductless') {
      const condensers =
        selectedSystems[activeSelectedSystem]?.configuration.condensers ?? [];
      const newAirHandlerAcc: Record<string, Record<number, number>> = {
        ...airHandlerAccessories
      };
      recommendedAccessories.forEach(
        ({ equipmentUniqueId, accessory, quantity }) => {
          if (isZoneEquipmentId(condensers, equipmentUniqueId)) {
            newAirHandlerAcc[equipmentUniqueId] = {
              ...(newAirHandlerAcc[equipmentUniqueId] ?? {}),
              [accessory.productId]: quantity
            };
          }
        }
      );
      setAirHandlerAccessories(newAirHandlerAcc);
      eqInteractedRef.current = true;

      const condenserTotals = new Map<string, Map<number, number>>();
      recommendedAccessories.forEach(
        ({ condenserUniqueId, equipmentUniqueId, accessory }) => {
          if (
            equipmentUniqueId &&
            isZoneEquipmentId(condensers, equipmentUniqueId)
          ) {
            const condenser = selectedSystems[
              activeSelectedSystem
            ]?.configuration.condensers.find(
              (c) => c.uniqueId === condenserUniqueId
            );
            if (condenser) {
              const total = condenser.zones.reduce((sum, z) => {
                return (
                  sum +
                  (newAirHandlerAcc[z.uniqueId]?.[accessory.productId] ?? 0)
                );
              }, 0);
              if (!condenserTotals.has(condenserUniqueId)) {
                condenserTotals.set(condenserUniqueId, new Map());
              }
              condenserTotals
                .get(condenserUniqueId)!
                .set(accessory.productId, total);
            }
          }
        }
      );

      const updatedSystems = produce(selectedSystems, (draft) => {
        recommendedAccessories.forEach(
          ({
            condenserUniqueId,
            equipmentUniqueId,
            accessory,
            quantity: entryQuantity
          }) => {
            const condenser = draft[
              activeSelectedSystem
            ]?.configuration.condensers.find(
              (c) => c.uniqueId === condenserUniqueId
            );
            if (condenser) {
              const quantity = isZoneEquipmentId(condensers, equipmentUniqueId)
                ? condenserTotals
                    .get(condenserUniqueId)
                    ?.get(accessory.productId) ?? entryQuantity
                : entryQuantity;
              const accessoryIndex = condenser.selectedAccessories.findIndex(
                (acc) => acc.productId === accessory.productId
              );
              if (accessoryIndex !== -1) {
                if (quantity > 0) {
                  // eslint-disable-next-line no-param-reassign
                  condenser.selectedAccessories[accessoryIndex].quantity =
                    quantity;
                } else {
                  condenser.selectedAccessories.splice(accessoryIndex, 1);
                }
              } else if (quantity > 0) {
                condenser.selectedAccessories.push({
                  ...accessory,
                  quantity
                });
              }
            }
          }
        );
      });
      dispatch(setSelectedSystems(updatedSystems));

      const activeSystem = updatedSystems[activeSelectedSystem];
      unquoteDuctless(activeSystem);
      updateQuotableSystemAccessories({
        uniqueId: activeSystem.uniqueId,
        accessories: buildAccessorySavePayload(activeSystem, newAirHandlerAcc)
      }).catch((error) => {
        console.error('Failed to update quotable system accessories:', error);
      });

      dispatch(
        updateSelectedSystem({
          uniqueId: activeSystem.uniqueId,
          quotableAccessories: getQuotableAccessories(activeSystem)
        })
      );
    }
  };

  const zoneDuctModeOptions = useMemo(
    () =>
      buildZoneDuctModeOptions({
        systems: selectedSystems,
        configurations,
        ductingKitProducts,
        airHandlerProducts,
        ductingComponents,
        zoneDuctModes
      }),
    [
      selectedSystems,
      configurations,
      ductingKitProducts,
      airHandlerProducts,
      ductingComponents,
      zoneDuctModes
    ]
  );

  const accessoryFromRecommendation = (
    rec: NonNullable<typeof accessoryRecommendations>[number],
    quantity: number
  ) => ({
    productId: rec.accessoryProductId,
    name: rec.accessoryName,
    shortName: rec.accessoryShortName,
    description: rec.accessoryDescription,
    price: rec.accessoryPrice,
    type: rec.accessoryOriginalType as AccessoryProductType['type'],
    productUrl: rec.accessoryProductUrl,
    photo: rec.accessoryPhoto,
    availableStock: rec.accessoryAvailableStock,
    quantity
  });

  const handleZoneDuctModeChange = (
    zoneUniqueId: string,
    modeKey: RadialDuctModeKeyType
  ) => {
    if (zoneDuctModes[zoneUniqueId] === modeKey) return;
    const overriddenModes = { ...zoneDuctModes, [zoneUniqueId]: modeKey };
    dispatch(setZoneDuctMode({ zoneUniqueId, modeKey }));
    if (activeSystemType !== 'ductless') return;

    const activeSystem = selectedSystems[activeSelectedSystem];
    if (!activeSystem) return;

    const { recommendations: newRecs, demotedKits: newDemotedKits } =
      buildDuctingComponentRecommendations({
        systems: selectedSystems,
        configurations,
        ductingKitProducts,
        airHandlerProducts,
        ductingComponents,
        zoneDuctModes: overriddenModes
      });

    const componentIds = new Set(
      ductingComponents.map((component) => component.productId)
    );
    const ductingKitIds = new Set(
      ductingKitProducts.map((kit) => kit.productId)
    );
    const zoneState = airHandlerAccessories[zoneUniqueId] ?? {};
    const hadSelection = Object.entries(zoneState).some(
      ([productId, quantity]) =>
        quantity > 0 &&
        (componentIds.has(Number(productId)) ||
          ductingKitIds.has(Number(productId)))
    );
    if (!hadSelection) return;

    const demotedKitIds = new Set(
      newDemotedKits
        .filter((demoted) => demoted.zoneUniqueId === zoneUniqueId)
        .map((demoted) => demoted.kitProductId)
    );

    const zoneRecs = newRecs.filter(
      (rec) =>
        rec.systemUniqueId === activeSystem.uniqueId &&
        rec.productType === 'air_handler' &&
        rec.uniqueId === zoneUniqueId
    );
    const consumableRecs = newRecs.filter(
      (rec) =>
        rec.systemUniqueId === activeSystem.uniqueId &&
        rec.productType === 'system'
    );

    const newZoneState: Record<number, number> = { ...zoneState };
    componentIds.forEach((productId) => {
      if (newZoneState[productId]) {
        newZoneState[productId] = 0;
      }
    });
    demotedKitIds.forEach((productId) => {
      if (newZoneState[productId]) {
        newZoneState[productId] = 0;
      }
    });
    zoneRecs.forEach((rec) => {
      newZoneState[rec.accessoryProductId] = rec.recommendedQuantity;
    });
    const newAirHandlerAcc = {
      ...airHandlerAccessories,
      [zoneUniqueId]: newZoneState
    };
    setAirHandlerAccessories(newAirHandlerAcc);
    eqInteractedRef.current = true;

    const owningCondenser = activeSystem.configuration.condensers.find(
      (condenser) =>
        condenser.zones.some((zone) => zone.uniqueId === zoneUniqueId)
    );
    const firstCondenserUniqueId =
      activeSystem.configuration.condensers[0]?.uniqueId;
    if (!owningCondenser) return;

    const affectedIds = new Set<number>([
      ...Object.keys(zoneState)
        .map(Number)
        .filter(
          (productId) =>
            componentIds.has(productId) || demotedKitIds.has(productId)
        ),
      ...zoneRecs.map((rec) => rec.accessoryProductId)
    ]);
    const consumableIds = new Set(
      consumableRecs.map((rec) => rec.accessoryProductId)
    );

    const updatedSystems = produce(selectedSystems, (draft) => {
      const draftSystem = draft[activeSelectedSystem];
      if (!draftSystem) return;
      const draftCondenser = draftSystem.configuration.condensers.find(
        (condenser) => condenser.uniqueId === owningCondenser.uniqueId
      );
      if (draftCondenser) {
        affectedIds.forEach((productId) => {
          const total = owningCondenser.zones.reduce(
            (sum, zone) =>
              sum + (newAirHandlerAcc[zone.uniqueId]?.[productId] ?? 0),
            0
          );
          const index = draftCondenser.selectedAccessories.findIndex(
            (acc) => acc.productId === productId
          );
          if (index !== -1) {
            if (total > 0) {
              // eslint-disable-next-line no-param-reassign
              draftCondenser.selectedAccessories[index].quantity = total;
            } else {
              draftCondenser.selectedAccessories.splice(index, 1);
            }
          } else if (total > 0) {
            const rec = zoneRecs.find(
              (candidate) => candidate.accessoryProductId === productId
            );
            if (rec) {
              draftCondenser.selectedAccessories.push(
                accessoryFromRecommendation(rec, total)
              );
            }
          }
        });
      }

      const firstDraftCondenser = draftSystem.configuration.condensers.find(
        (condenser) => condenser.uniqueId === firstCondenserUniqueId
      );
      if (firstDraftCondenser) {
        consumableRecs.forEach((rec) => {
          const index = firstDraftCondenser.selectedAccessories.findIndex(
            (acc) => acc.productId === rec.accessoryProductId
          );
          if (index !== -1) {
            // eslint-disable-next-line no-param-reassign
            firstDraftCondenser.selectedAccessories[index].quantity =
              rec.recommendedQuantity;
          } else {
            firstDraftCondenser.selectedAccessories.push(
              accessoryFromRecommendation(rec, rec.recommendedQuantity)
            );
          }
        });
        for (
          let index = firstDraftCondenser.selectedAccessories.length - 1;
          index >= 0;
          index -= 1
        ) {
          const acc = firstDraftCondenser.selectedAccessories[index];
          const trackedPerZone = activeSystem.configuration.condensers.some(
            (condenser) =>
              condenser.zones.some(
                (zone) =>
                  (newAirHandlerAcc[zone.uniqueId]?.[acc.productId] ?? 0) > 0
              )
          );
          if (
            componentIds.has(acc.productId) &&
            !trackedPerZone &&
            !consumableIds.has(acc.productId)
          ) {
            firstDraftCondenser.selectedAccessories.splice(index, 1);
          }
        }
      }
    });
    dispatch(setSelectedSystems(updatedSystems));

    const updatedActiveSystem = updatedSystems[activeSelectedSystem];
    unquoteDuctless(updatedActiveSystem);
    updateQuotableSystemAccessories({
      uniqueId: updatedActiveSystem.uniqueId,
      accessories: buildAccessorySavePayload(
        updatedActiveSystem,
        newAirHandlerAcc
      )
    }).catch((error) => {
      console.error('Failed to update quotable system accessories:', error);
    });
    dispatch(
      updateSelectedSystem({
        uniqueId: updatedActiveSystem.uniqueId,
        quotableAccessories: getQuotableAccessories(updatedActiveSystem)
      })
    );
  };

  const buildDuctModeHeaderExtras = (): Map<string, ReactNode> => {
    const extras = new Map<string, ReactNode>();
    zoneDuctModeOptions.forEach((option) => {
      const selectedDetail = option.details[option.selectedKey];
      if (!selectedDetail) return;
      const showToggles = option.passingModeCount >= 2;
      const buttonClass = (key: RadialDuctModeKeyType): string =>
        option.selectedKey === key
          ? 'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold bg-blue-500 text-white'
          : 'inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-semibold bg-white text-gray-600 border border-gray-300 hover:bg-gray-100';
      const availabilityIcon = (
        key: RadialDuctModeKeyType,
        onBlue: boolean
      ) => {
        const detail = option.details[key];
        if (!detail) return null;
        const icon =
          detail.allPartsAvailable === false ? (
            <ExclamationTriangleIcon className='h-3.5 w-3.5 shrink-0 text-amber-500' />
          ) : (
            <CheckCircleIcon
              className={`h-3.5 w-3.5 shrink-0 ${
                onBlue ? 'text-green-200' : 'text-green-600'
              }`}
            />
          );
        return (
          <span className='relative inline-flex'>
            {icon}
            <Tooltip
              interactive
              options={{ placement: 'bottom-end' }}
              panelClassName='z-50 flex max-h-96 w-80 flex-col gap-2 overflow-y-auto rounded bg-white p-3 text-left shadow-lg ring-1 ring-gray-200'
            >
              <span className='text-xs font-semibold uppercase tracking-wider text-gray-500'>
                Calculated Duct Supplies — {ZONE_DUCT_MODE_LABELS[key]}
              </span>
              {detail.supplies.supplyRuns.map((run) => (
                <DuctRunSuppliesGroup key={run.key} run={run} />
              ))}
              {detail.supplies.returnRuns.map((run) => (
                <DuctRunSuppliesGroup key={run.key} run={run} />
              ))}
            </Tooltip>
          </span>
        );
      };
      extras.set(
        option.zoneUniqueId,
        <div className='flex max-w-xs flex-col items-end gap-1'>
          {showToggles ? (
            <div className='flex items-center gap-1'>
              {DUCT_MODE_KEYS.filter(
                (key) => option.details[key] !== undefined
              ).map((key) => (
                <button
                  key={key}
                  type='button'
                  className={buttonClass(key)}
                  onClick={() =>
                    handleZoneDuctModeChange(option.zoneUniqueId, key)
                  }
                >
                  {ZONE_DUCT_MODE_LABELS[key]}
                  {availabilityIcon(key, option.selectedKey === key)}
                </button>
              ))}
            </div>
          ) : (
            <span className='inline-flex items-center gap-1 rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600'>
              {ZONE_DUCT_MODE_LABELS[option.selectedKey]}
              {availabilityIcon(option.selectedKey, false)}
            </span>
          )}
          {selectedDetail.allPartsAvailable === false && (
            <span className='text-right text-xs text-amber-600'>
              {`Alpine stocks ${selectedDetail.stockedCount} of ${selectedDetail.totalCount} calculated parts. ${DUCT_SOURCING_NOTICE}`}
            </span>
          )}
        </div>
      );
    });
    return extras;
  };

  const handleAccessoryChange = (
    condenserId: string,
    accessory: AccessoryProductType,
    quantity: number,
    equipmentUniqueId?: string
  ) => {
    if (activeSystemType === 'ductless') {
      let condenserQuantity = quantity;
      let newAirHandlerAcc = airHandlerAccessories;

      if (equipmentUniqueId && equipmentUniqueId !== condenserId) {
        eqInteractedRef.current = true;

        newAirHandlerAcc = {
          ...airHandlerAccessories,
          [equipmentUniqueId]: {
            ...(airHandlerAccessories[equipmentUniqueId] ?? {}),
            [accessory.productId]: quantity
          }
        };

        const condenser = selectedSystems[
          activeSelectedSystem
        ]?.configuration.condensers.find((c) => c.uniqueId === condenserId);
        if (condenser) {
          condenserQuantity = condenser.zones.reduce((sum, z) => {
            return (
              sum + (newAirHandlerAcc[z.uniqueId]?.[accessory.productId] ?? 0)
            );
          }, 0);
        }

        setAirHandlerAccessories(newAirHandlerAcc);
      }

      const updatedSystems = produce(selectedSystems, (draft) => {
        const condenser = draft[
          activeSelectedSystem
        ]?.configuration.condensers.find((c) => c.uniqueId === condenserId);
        if (condenser) {
          if (condenserQuantity > 0) {
            if (draft[activeSelectedSystem])
              // eslint-disable-next-line no-param-reassign
              draft[activeSelectedSystem].noAccessoriesNeeded = false;
            // eslint-disable-next-line no-param-reassign
            condenser.noAccessoriesNeeded = false;
          }
          const category = Object.keys(accessoryCategories).find((key) =>
            accessoryCategories[key].includes(accessory.type)
          ) as AccessoryCategoryType | undefined;
          const accessoryIndex = condenser.selectedAccessories.findIndex(
            (acc) => acc.productId === accessory.productId
          );
          if (accessoryIndex !== -1) {
            if (condenserQuantity > 0) {
              // eslint-disable-next-line no-param-reassign
              condenser.selectedAccessories[accessoryIndex].quantity =
                condenserQuantity;
            } else {
              condenser.selectedAccessories.splice(accessoryIndex, 1);
            }
          } else if (condenserQuantity > 0) {
            condenser.selectedAccessories.push({
              ...accessory,
              quantity: condenserQuantity
            });
          }
          if (category) {
            // eslint-disable-next-line no-param-reassign
            condenser.accessoriesCompleted =
              condenser.accessoriesCompleted.filter((c) => c !== category);
            if (
              condenser.selectedAccessories.some((acc) =>
                accessoryCategories[category]?.includes(acc.type)
              )
            ) {
              condenser.accessoriesCompleted.push(category);
              // eslint-disable-next-line no-param-reassign
              condenser.accessoriesNotNeeded =
                condenser.accessoriesNotNeeded.filter((c) => c !== category);
            }
          }
        }
      });

      dispatch(setSelectedSystems(updatedSystems));

      const activeSystem = updatedSystems[activeSelectedSystem];
      unquoteDuctless(activeSystem);
      updateQuotableSystemAccessories({
        uniqueId: activeSystem.uniqueId,
        accessories: buildAccessorySavePayload(activeSystem, newAirHandlerAcc)
      }).catch((error) => {
        console.error('Failed to update quotable system accessories:', error);
      });

      dispatch(
        updateSelectedSystem({
          uniqueId: activeSystem.uniqueId,
          quotableAccessories: getQuotableAccessories(activeSystem)
        })
      );
    } else {
      const updatedDuctedSystems = produce(selectedDuctedSystems, (draft) => {
        const condenser = draft[activeDuctedSystem].config.condensers.find(
          (c) => c.uniqueId === condenserId
        );
        if (condenser) {
          if (quantity > 0) {
            // eslint-disable-next-line no-param-reassign
            draft[activeDuctedSystem].noAccessoriesNeeded = false;
            // eslint-disable-next-line no-param-reassign
            condenser.noAccessoriesNeeded = false;
          }
          const category = Object.keys(accessoryCategories).find((key) =>
            accessoryCategories[key].includes(accessory.type)
          ) as AccessoryCategoryType | undefined;
          const accessoryIndex = condenser.selectedAccessories.findIndex(
            (acc) => acc.productId === accessory.productId
          );
          if (accessoryIndex !== -1) {
            if (quantity > 0) {
              // eslint-disable-next-line no-param-reassign
              condenser.selectedAccessories[accessoryIndex].quantity = quantity;
            } else {
              condenser.selectedAccessories.splice(accessoryIndex, 1);
            }
          } else if (quantity > 0) {
            condenser.selectedAccessories.push({
              ...accessory,
              quantity
            });
          }
          if (category) {
            // eslint-disable-next-line no-param-reassign
            condenser.accessoriesCompleted =
              condenser.accessoriesCompleted.filter((c) => c !== category);
            if (
              condenser.selectedAccessories.some((acc) =>
                accessoryCategories[category]?.includes(acc.type)
              )
            ) {
              condenser.accessoriesCompleted.push(category);
              // eslint-disable-next-line no-param-reassign
              condenser.accessoriesNotNeeded = draft[
                activeDuctedSystem
              ]?.accessoriesNotNeeded.filter((c) => c !== category);
            }
          }
        }
      });

      dispatch(setSelectedDuctedSystems(updatedDuctedSystems));

      const activeDucted = updatedDuctedSystems[activeDuctedSystem];
      if (activeDucted) {
        unquoteDucted(activeDucted);
        dispatch(
          updateSelectedDuctedSystem({
            id: activeDucted.id,
            quotableAccessories: getQuotableAccessories(activeDucted)
          })
        );
      }
    }
  };

  const handleBatchAccessoryChange = (
    condenserId: string,
    changes: Array<{ accessory: AccessoryProductType; quantity: number }>,
    equipmentUniqueId?: string
  ) => {
    if (activeSystemType === 'ductless') {
      let newAirHandlerAcc = airHandlerAccessories;

      if (equipmentUniqueId && equipmentUniqueId !== condenserId) {
        eqInteractedRef.current = true;

        const zoneOverrides: Record<number, number> = {};
        for (const change of changes) {
          zoneOverrides[change.accessory.productId] = change.quantity;
        }

        newAirHandlerAcc = {
          ...airHandlerAccessories,
          [equipmentUniqueId]: {
            ...(airHandlerAccessories[equipmentUniqueId] ?? {}),
            ...zoneOverrides
          }
        };

        setAirHandlerAccessories(newAirHandlerAcc);
      }

      const updatedSystems = produce(selectedSystems, (draft) => {
        const condenser = draft[
          activeSelectedSystem
        ]?.configuration.condensers.find((c) => c.uniqueId === condenserId);
        if (condenser) {
          changes.forEach((change) => {
            let condenserQuantity = change.quantity;

            if (equipmentUniqueId && equipmentUniqueId !== condenserId) {
              const { productId } = change.accessory;
              condenserQuantity = condenser.zones.reduce((sum, zone) => {
                return (
                  sum + (newAirHandlerAcc[zone.uniqueId]?.[productId] ?? 0)
                );
              }, 0);
            }

            if (condenserQuantity > 0 && draft[activeSelectedSystem]) {
              // eslint-disable-next-line no-param-reassign
              draft[activeSelectedSystem].noAccessoriesNeeded = false;
            }

            applyAccessoryQuantityToCondenser(
              condenser,
              change.accessory,
              condenserQuantity
            );
          });
        }
      });

      dispatch(setSelectedSystems(updatedSystems));

      const activeSystemVal = updatedSystems[activeSelectedSystem];
      unquoteDuctless(activeSystemVal);
      updateQuotableSystemAccessories({
        uniqueId: activeSystemVal.uniqueId,
        accessories: buildAccessorySavePayload(
          activeSystemVal,
          newAirHandlerAcc
        )
      }).catch((error) => {
        console.error('Failed to update quotable system accessories:', error);
      });

      dispatch(
        updateSelectedSystem({
          uniqueId: activeSystemVal.uniqueId,
          quotableAccessories: getQuotableAccessories(activeSystemVal)
        })
      );
    } else {
      const updatedDuctedSystems = produce(selectedDuctedSystems, (draft) => {
        const ductedSystem = draft[activeDuctedSystem];
        const condenser = ductedSystem?.config.condensers.find(
          (item) => item.uniqueId === condenserId
        );
        if (!condenser) {
          return;
        }
        changes.forEach((change) => {
          if (change.quantity > 0 && ductedSystem) {
            // eslint-disable-next-line no-param-reassign
            ductedSystem.noAccessoriesNeeded = false;
          }
          applyAccessoryQuantityToCondenser(
            condenser,
            change.accessory,
            change.quantity
          );
        });
      });
      dispatch(setSelectedDuctedSystems(updatedDuctedSystems));

      const activeDucted = updatedDuctedSystems[activeDuctedSystem];
      if (activeDucted) {
        unquoteDucted(activeDucted);
        dispatch(
          updateSelectedDuctedSystem({
            id: activeDucted.id,
            quotableAccessories: getQuotableAccessories(activeDucted)
          })
        );
      }
    }
  };

  const activeSystem =
    activeSystemType === 'ductless'
      ? selectedSystems[activeSelectedSystem]
      : undefined;

  const activeSystemUsesDuctedAccessoryRules =
    activeSystemType === 'ductless' &&
    activeSystem !== undefined &&
    usesDuctedAccessoryRules(activeSystem);

  const condenserAccessories = useMemo(
    () => condenserAccessoryEntries(activeCondensers),
    [activeCondensers]
  );
  const { manualAccessories, selectedProductIds, handleManualAccessoryChange } =
    useManualAccessories({
      quotableSystemUniqueId: activeSystem?.uniqueId,
      accessories: condenserAccessories,
      onChange: (product, quantity, ownerUniqueId) => {
        const condenserUniqueId =
          ownerUniqueId ?? activeCondensers[0]?.uniqueId;
        if (!condenserUniqueId) return;
        handleAccessoryChange(condenserUniqueId, product, quantity);
      }
    });

  const textKeys = useAppSelector((state) => state.data.textKeys);
  const hasAccessorizableSystems =
    selectedDuctedSystems.length > 0 || selectedSystems.length > 0;
  const tipSections = useMemo(
    () =>
      getAccessoriesTipSections({
        textKeys,
        ready: hasAccessorizableSystems && !isLoadingAccessories,
        hasLineSetSection: activeSystemType === 'ductless' && hasCondensers,
        hasAccessorySections: nonLineSetPanelData.length > 0
      }),
    [
      textKeys,
      hasAccessorizableSystems,
      isLoadingAccessories,
      activeSystemType,
      hasCondensers,
      nonLineSetPanelData
    ]
  );
  const { currentStepTipSequence, reopenTipSequence } = useStepTipSequence({
    sections: tipSections,
    currentSectionId: 'accessories',
    currentStepComplete: false
  });

  const proceedToNextAccessoriesStep = useCallback(() => {
    if (selectedSystems.length > 0) {
      const currentSystem = selectedSystems[activeSelectedSystem];
      const isAlreadyReviewed =
        quotableSystems[currentSystem?.uniqueId]?.accessoriesReviewed ?? false;
      if (currentSystem && !isAlreadyReviewed) {
        dispatch(
          setSystemAccessoriesReviewed({
            uniqueId: currentSystem.uniqueId,
            accessoriesReviewed: true
          })
        );
        updateAccessoriesReviewed({
          uniqueId: currentSystem.uniqueId,
          accessoriesReviewed: true
        }).catch((error) => {
          console.error('Failed to update accessories reviewed:', error);
        });
      }

      const nextSystemIndex = activeSelectedSystem + 1;
      if (nextSystemIndex < selectedSystems.length) {
        dispatch(setActiveSelectedSystem(nextSystemIndex));
        return;
      }
    }

    if (selectedDuctedSystems.length > 0) {
      // ducted systems handled similarly
    }

    dispatch(setActiveTab('quote'));
  }, [
    selectedSystems,
    activeSelectedSystem,
    quotableSystems,
    selectedDuctedSystems,
    dispatch,
    updateAccessoriesReviewed
  ]);

  const handleNavNext = useCallback(() => {
    if (categoriesWithUnselectedRecommendations.length > 0) {
      conflictModal({
        title: 'Missing Accessory Selections',
        message: createElement(
          'div',
          { className: 'text-left' },
          createElement(
            'p',
            { className: 'mb-4' },
            'The following categories have recommended accessories but no selection has been made:'
          ),
          createElement(
            'ul',
            { className: 'list-disc pl-5 mb-4' },
            ...categoriesWithUnselectedRecommendations.map((category) =>
              createElement(
                'li',
                { key: category, className: 'text-gray-700' },
                category
              )
            )
          ),
          createElement(
            'p',
            null,
            'Would you like to continue without selecting accessories for these categories?'
          )
        ),
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        confirmAction: proceedToNextAccessoriesStep,
        cancelAction: () => {}
      });
      return;
    }
    proceedToNextAccessoriesStep();
  }, [categoriesWithUnselectedRecommendations, proceedToNextAccessoriesStep]);

  const systemDesignLocked = isSystemDesignLocked(useSystemDesignBypassMode());

  const handleNavBack = useCallback(() => {
    if (selectedSystems.length > 1 && activeSelectedSystem > 0) {
      dispatch(setActiveSelectedSystem(activeSelectedSystem - 1));
      return;
    }
    const target =
      systemsToCompare.length > 0
        ? 'chooseSystems'
        : getEmptyComparisonBackTarget(systemDesignLocked);
    dispatch(setActiveTab(target));
  }, [
    dispatch,
    selectedSystems.length,
    activeSelectedSystem,
    systemsToCompare.length,
    systemDesignLocked
  ]);

  const nextButtonText =
    selectedSystems.length > 1 &&
    activeSelectedSystem + 1 < selectedSystems.length
      ? 'Next'
      : 'Next Section';

  useFooterConfig(
    useMemo(
      () => ({
        onBack: handleNavBack,
        onNext: handleNavNext,
        backText: 'Back',
        nextText: nextButtonText,
        nextVariant:
          nextButtonText === 'Next Section'
            ? ('green' as const)
            : ('blue' as const),
        showBackArrow: true
      }),
      [handleNavBack, handleNavNext, nextButtonText]
    )
  );

  if (
    activeSystemType === 'ductless' &&
    !activeSystem &&
    selectedSystems.length > 0
  ) {
    return null;
  }

  return (
    <div className='mx-auto flex max-w-[1400px] flex-col gap-5'>
      <div className='flex items-center gap-2 text-3xl font-bold text-gray-900'>
        Add Accessories
        {currentStepTipSequence && (
          <button
            type='button'
            className='relative flex items-center text-gray-400 hover:text-blue-500'
            onClick={reopenTipSequence}
            aria-label='Show page tips'
          >
            <QuestionMarkCircleIcon className='h-7 w-7' />
          </button>
        )}
      </div>
      {selectedDuctedSystems.length < 1 && selectedSystems.length < 1 ? (
        <InternalNote>
          No systems selected. Please select at least one system to accessorize.
        </InternalNote>
      ) : (
        <div ref={animate} className='flex grow flex-col gap-5'>
          <div className='flex items-center justify-between'>
            <div className='flex flex-wrap items-center gap-3'>
              <span className='font-semibold'>Systems to Accessorize:</span>
              {selectedDuctedSystems.map((system) => (
                <DuctedSystemOptionComponent
                  key={system.id}
                  config={system.config}
                  system={system}
                  isAccessoryPage
                  hideDeletable
                />
              ))}
              {selectedSystems.map((system) => (
                <SelectedSystemComponent
                  key={system.uniqueId}
                  system={system}
                  isAccessoryPage
                  hideDeletable
                  hideRenamable
                />
              ))}
            </div>
            {activeSystemUsesDuctedAccessoryRules && activeSystem ? (
              <DuctedIncludeAllRecommendedToggle system={activeSystem} />
            ) : (
              <IncludeAllRecommendedToggle
                value={allRecommendedIncluded}
                onChange={handleIncludeAllRecommended}
              />
            )}
          </div>
          <div className='flex flex-col gap-5'>
            {activeSystemUsesDuctedAccessoryRules && activeSystem && (
              <DuctedAccessoriesPage
                key={activeSystem.uniqueId}
                system={activeSystem}
              />
            )}
            {!activeSystemUsesDuctedAccessoryRules &&
              (isLoadingAccessories ? (
                <div className='flex items-center justify-center py-12'>
                  <svg
                    className='h-8 w-8 animate-spin text-gray-400'
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                </div>
              ) : (
                <>
                  {activeSystemType === 'ductless' && hasCondensers && (
                    <div id='line-set-drawer'>
                      <AccessoryDrawer
                        title='Line Sets & Wire'
                        description='Select line sets and communication wire for each air handler.'
                        itemSelected={lineSetPanelData?.isCompleted ?? false}
                        notNeeded={!lineSetAllRecommendedSelected}
                        handleNotNeeded={(notNeeded) => {
                          const include = !notNeeded;
                          setLineSetIncludeRecommended(include);
                          if (include) {
                            // Use cartable accessories (excludes included-with-line-set items)
                            handleIncludeRecommendedToggle(
                              'line_set',
                              true,
                              lineSetCartableRecommendedAccessories.map(
                                (entry) => ({
                                  ...entry,
                                  quantity: 1
                                })
                              )
                            );
                          } else {
                            const allLineSetProducts = (
                              lineSetPanelData?.equipment ?? []
                            ).flatMap((item) =>
                              Array.from(item.accessoryProducts.values()).map(
                                (accessory) => ({
                                  condenserUniqueId: item.condenserUniqueId,
                                  equipmentUniqueId: item.equipmentUniqueId,
                                  accessory,
                                  quantity: 0
                                })
                              )
                            );
                            handleIncludeRecommendedToggle(
                              'line_set',
                              false,
                              allLineSetProducts
                            );
                          }
                        }}
                        switchDisabled={lineSetSwitchDisabled}
                        required={hasSelectableLineSets}
                      >
                        <div className='divide-y divide-gray-200'>
                          {activeCondensers.map(
                            (condenser, condenserIndex, condensers) => {
                              const airHandlerStartIndex = condensers
                                .slice(0, condenserIndex)
                                .reduce((sum, c) => sum + c.zones.length, 0);
                              return (
                                <LineSetOption
                                  key={`${activeSystemType}-${activeSelectedSystem}-${condenser.uniqueId}`}
                                  condenser={condenser}
                                  airHandlerStartIndex={airHandlerStartIndex}
                                  lineSetEquipment={
                                    lineSetPanelData?.equipment ?? []
                                  }
                                  onAccessoryChange={handleAccessoryChange}
                                  onBatchAccessoryChange={
                                    handleBatchAccessoryChange
                                  }
                                  includeRecommended={lineSetIncludeRecommended}
                                />
                              );
                            }
                          )}
                        </div>
                      </AccessoryDrawer>
                    </div>
                  )}
                  {nonLineSetPanelData.map((panelData, index) => (
                    <div
                      key={panelData.accessoryType.code}
                      id={index === 0 ? 'first-accessory-section' : undefined}
                    >
                      <AccessoryTypePanel
                        accessoryType={panelData.accessoryType}
                        equipment={panelData.equipment}
                        onAccessoryChange={handleAccessoryChange}
                        onBatchAccessoryChange={handleBatchAccessoryChange}
                        onIncludeRecommendedToggle={
                          handleIncludeRecommendedToggle
                        }
                        isCompleted={panelData.isCompleted}
                        includedAccessoryTypes={includedAccessoryTypesMap}
                        equipmentHeaderExtras={
                          panelData.accessoryType.code === 'air_distribution'
                            ? buildDuctModeHeaderExtras()
                            : undefined
                        }
                      />
                    </div>
                  ))}
                  {hasCondensers && (
                    <AdditionalAccessoriesDrawer
                      accessories={manualAccessories}
                      selectedProductIds={selectedProductIds}
                      onChange={handleManualAccessoryChange}
                    />
                  )}
                </>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
